import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Buildings, ArrowsClockwise, Coins, ShieldCheck, Prohibit, Play, EnvelopeSimple, ChartBar, DownloadSimple, Info, Eye, FileArrowDown, UserPlus, ListChecks, Trash } from "@phosphor-icons/react";
import api, { formatApiError, downloadBlob, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SecurityPosture from "../components/SecurityPosture";
import PlatformBaseline from "../components/PlatformBaseline";
import SecurityAccessLog from "../components/SecurityAccessLog";
import TestScriptsPanel from "../components/TestScriptsPanel";
import ConsoleUsers from "../components/ConsoleUsers";
import AuditTrailPanel from "../components/AuditTrailPanel";
import AnnouncementsManager from "../components/AnnouncementsManager";
import ConsoleOffboarding from "../components/ConsoleOffboarding";
import ConsoleTrials from "../components/ConsoleTrials";
import ConsoleBilling from "../components/ConsoleBilling";
import ConsoleInvoices from "../components/ConsoleInvoices";
import ConsoleNerc from "../components/ConsoleNerc";
import StandardsManager from "../components/StandardsManager";
import ConsoleDuplicateCleanup from "../components/ConsoleDuplicateCleanup";
import ConsoleClaroTickets from "../components/ConsoleClaroTickets";
import ConsoleClaroInvites from "../components/ConsoleClaroInvites";
import ConsoleAiTransparency from "../components/ConsoleAiTransparency";
import ConsoleKeySecurity from "../components/ConsoleKeySecurity";
import PlatformModePanel from "../components/PlatformModePanel";
import { NewTenantDialog, TenantAdminsDialog } from "../components/ConsoleTenantOnboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

const AI_COST_HINTS = {
  credits: "In-app AI credits consumed — a flat 1 credit per gated AI action. This is what you bill your customers; it's independent of the real token cost you pay on the Universal Key.",
  calls: "The number of individual requests sent to the language model. A single action can trigger several calls (e.g. one per requirement in a ClaroLens review), so calls are usually higher than credits.",
};

function HintLabel({ children, hint, testid }) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-help" data-testid={testid}>{children}<Info size={12} weight="bold" className="opacity-50" /></span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-xs bg-popover text-popover-foreground border border-border shadow-lg p-2.5 rounded-none z-50 text-xs leading-relaxed normal-case tracking-normal font-normal">
          {hint}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trialing: "bg-sky-50 text-sky-700 border-sky-200",
  past_due: "bg-amber-50 text-amber-700 border-amber-200",
  trial_expired: "bg-orange-50 text-orange-700 border-orange-200",
  suspended: "bg-red-50 text-red-700 border-red-200",
  canceled: "bg-muted text-muted-foreground border-border",
};
const FEATURE_KEYS = ["exports", "evidence_links", "rsaw_populator", "claroflow", "ai"];

export default function ClaroConsole() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [showAudit, setShowAudit] = useState(false);
  const [data, setData] = useState({ tenants: [], plans: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [creditFor, setCreditFor] = useState(null);
  const [featFor, setFeatFor] = useState(null);
  const [newTenantOpen, setNewTenantOpen] = useState(false);
  const [adminsFor, setAdminsFor] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reconBusy, setReconBusy] = useState(false);
  const [cleanupMode, setCleanupMode] = useState(false);
  const refreshCleanup = useCallback(() => {
    api.get("/console/platform-settings").then((r) => setCleanupMode(!!r.data?.cleanup_mode)).catch(() => {});
  }, []);
  useEffect(() => { refreshCleanup(); }, [refreshCleanup]);

  const deleteEntity = async (t) => {
    const typed = window.prompt(`⚠ PERMANENT DELETE\n\nThis purges "${t.name}" and ALL its data (users, documents, evidence, tickets, investigations, SDP). This cannot be undone.\n\nType the entity's exact name to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== (t.name || "").trim()) { toast.error("Name did not match — deletion cancelled."); return; }
    setBusy(t.id);
    try {
      const { data } = await api.delete(`/console/tenants/${t.id}`, { params: { confirm_name: typed.trim() } });
      toast.success(`Purged "${data.entity}" — ${data.records_purged} records, ${data.users_deleted} users removed.`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(""); }
  };

  const sendReminderPreview = async () => {
    setReminderBusy(true);
    try {
      const { data } = await api.post("/console/reminder-preview");
      toast.success(`Test reminder sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send preview");
    } finally { setReminderBusy(false); }
  };

  const reconcileMemberships = async () => {
    setReconBusy(true);
    try {
      const { data: prev } = await api.post("/console/reconcile-memberships", null, { params: { dry_run: true } });
      const willChange = prev.orphan_refs_removed + prev.users_flagged + prev.users_defaulted + prev.defaults_fixed;
      if (!willChange) { toast.success("Memberships are already clean — no orphaned entities found."); return; }
      const ok = window.confirm(
        `Reconcile memberships across ${prev.users_scanned} users?\n\n` +
        `• ${prev.orphan_refs_removed} orphaned entity reference(s) will be removed\n` +
        `• ${prev.defaults_fixed} dangling default(s) will be fixed\n` +
        `• ${prev.users_flagged} user(s) with no valid entity will be FLAGGED (24h grace, then default)\n` +
        `• ${prev.users_defaulted} previously-flagged user(s) past 24h will fall back to the default entity\n\n` +
        `This is safe to re-run. Proceed?`);
      if (!ok) return;
      const { data } = await api.post("/console/reconcile-memberships");
      toast.success(`Reconciled: removed ${data.orphan_refs_removed} orphan refs, fixed ${data.defaults_fixed} defaults, flagged ${data.users_flagged}, defaulted ${data.users_defaulted}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reconcile failed");
    } finally { setReconBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/console/tenants");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load tenants");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (tid, body, note) => {
    setBusy(tid);
    try {
      await api.patch(`/console/tenants/${tid}`, body);
      toast.success(note || "Updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally { setBusy(""); }
  };

  const planLabel = useMemo(() => Object.fromEntries((data.plans || []).map((p) => [p.key, p.label])), [data.plans]);

  if (!user?.is_superadmin) {
    return <div className="p-8 text-sm text-muted-foreground" data-testid="console-forbidden">ClaroConsole is restricted to Claro administrators.</div>;
  }

  return (
    <div className="space-y-6 px-4 pt-4 md:px-0 md:pt-0" data-testid="claro-console">
      <ConsoleClaroInvites />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2"><Buildings size={26} weight="duotone" /> ClaroConsole</h1>
          <p className="text-sm text-muted-foreground mt-1">Master control panel — {data.tenants.length} tenant organization{data.tenants.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-none" onClick={() => nav("/console/soc2")} data-testid="console-soc2-hub">
            <ShieldCheck size={15} className="mr-1" /> SOC 2 Hub
          </Button>
          <Button variant="outline" className="rounded-none" onClick={() => nav("/console/claro-shield")} data-testid="console-claro-shield">
            <ShieldCheck size={15} className="mr-1" /> The Claro Shield
          </Button>
          <Button variant="outline" className="rounded-none" onClick={() => setShowAudit((v) => !v)} data-testid="console-audit-toggle">
            <ListChecks size={15} className="mr-1" /> {showAudit ? "Hide Audit Trail" : "Audit Trail"}
          </Button>
          <Button className="rounded-none bg-primary hover:bg-primary/90" onClick={() => setNewTenantOpen(true)} data-testid="console-new-tenant">
            <Buildings size={15} className="mr-1" /> New Tenant
          </Button>
          <Button variant="outline" className="rounded-none" onClick={sendReminderPreview} disabled={reminderBusy} data-testid="console-reminder-preview">
            <EnvelopeSimple size={15} className="mr-1" /> {reminderBusy ? "Sending…" : "Send me a test reminder"}
          </Button>
          {cleanupMode && (
            <Button variant="outline" className="rounded-none border-amber-400 text-amber-700 hover:bg-amber-50" onClick={reconcileMemberships} disabled={reconBusy} data-testid="console-reconcile-memberships">
              <ArrowsClockwise size={15} className="mr-1" /> {reconBusy ? "Reconciling…" : "Reconcile Memberships"}
            </Button>
          )}
          <Button variant="outline" className="rounded-none" onClick={load} data-testid="console-refresh">
            <ArrowsClockwise size={15} className="mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {showAudit && <AuditTrailPanel />}

      <Tabs defaultValue="tenants" className="w-full" onValueChange={() => refreshCleanup()}>
        <TabsList className="rounded-none h-auto flex-wrap justify-start bg-muted">
          <TabsTrigger value="tenants" data-testid="console-tab-tenants" className="rounded-none">Tenants</TabsTrigger>
          <TabsTrigger value="ai" data-testid="console-tab-ai" className="rounded-none">AI Usage</TabsTrigger>
          <TabsTrigger value="ai-transparency" data-testid="console-tab-ai-transparency" className="rounded-none">AI Transparency</TabsTrigger>
          <TabsTrigger value="activity" data-testid="console-tab-activity" className="rounded-none">Activity</TabsTrigger>
          <TabsTrigger value="announcements" data-testid="console-tab-announcements" className="rounded-none">Announcements</TabsTrigger>
          <TabsTrigger value="offboarding" data-testid="console-tab-offboarding" className="rounded-none">Offboarding</TabsTrigger>
          <TabsTrigger value="trials" data-testid="console-tab-trials" className="rounded-none">Trials</TabsTrigger>
          <TabsTrigger value="billing" data-testid="console-tab-billing" className="rounded-none">Billing &amp; Provisioning</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="console-tab-invoices" className="rounded-none">Invoices</TabsTrigger>
          <TabsTrigger value="nerc" data-testid="console-tab-nerc" className="rounded-none">NERC Standards</TabsTrigger>
          <TabsTrigger value="key-security" data-testid="console-tab-key-security" className="rounded-none">Key Security</TabsTrigger>
          <TabsTrigger value="platform" data-testid="console-tab-platform" className="rounded-none">Platform Mode</TabsTrigger>
          <TabsTrigger value="security" data-testid="console-tab-security" className="rounded-none">Software Security</TabsTrigger>
          <TabsTrigger value="users" data-testid="console-tab-users" className="rounded-none">Users</TabsTrigger>
          <TabsTrigger value="test-scripts" data-testid="console-tab-test-scripts" className="rounded-none">Test Scripts</TabsTrigger>
          <TabsTrigger value="claro-tickets" data-testid="console-tab-claro-tickets" className="rounded-none">Claro Tickets</TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="space-y-6 mt-6">
      <ViewAsCustomerToggle />

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="console-tenant-table">
          <thead>
            <tr className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="p-3">Company</th>
              <th className="p-3">Subdomain</th>
              <th className="p-3 w-48">Plan</th>
              <th className="p-3 w-40">Status</th>
              <th className="p-3">AI Credits (base + top-up)</th>
              <th className="p-3">Billing</th>
              <th className="p-3 text-right">Controls</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && data.tenants.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No tenants yet.</td></tr>}
            {data.tenants.map((t) => (
              <tr key={t.id} className="border-t border-border align-top" data-testid={`console-row-${t.id}`}>
                <td className="p-3">
                  <div className="font-semibold text-foreground">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.user_count} user{t.user_count === 1 ? "" : "s"}</div>
                </td>
                <td className="p-3">
                  <span className="text-xs font-mono">{t.slug || "—"}<span className="text-muted-foreground">.clarocompliance.net</span></span>
                </td>
                <td className="p-3">
                  <Select value={t.plan} onValueChange={(v) => patch(t.id, { plan: v }, `Plan → ${planLabel[v] || v}`)}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`console-plan-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(data.plans || []).map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3">
                  <Select value={t.plan_status} onValueChange={(v) => patch(t.id, { plan_status: v }, `Status → ${v}`)}>
                    <SelectTrigger className={`rounded-none h-8 text-xs border ${STATUS_STYLE[t.plan_status] || ""}`} data-testid={`console-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {t.locked && <div className="text-[10px] text-red-600 mt-1">read-only lock</div>}
                </td>
                <td className="p-3">
                  <div className="font-semibold" data-testid={`console-credits-${t.id}`}>{t.ai_credits_total.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{t.ai_credits_base.toLocaleString()} base + {t.ai_credits_topup.toLocaleString()} top-up</div>
                </td>
                <td className="p-3 text-xs">
                  <span className={`px-1.5 py-0.5 border rounded-sm ${t.billing_mode === "manual_wire" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-muted text-muted-foreground border-border"}`}>
                    {t.billing_mode === "manual_wire" ? "Wire" : "Invoice"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1 justify-end">
                    <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" disabled={busy === t.id}
                            data-testid={`console-credits-btn-${t.id}`} onClick={() => setCreditFor(t)}>
                      <Coins size={13} className="mr-1" /> Credits
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" disabled={busy === t.id}
                            data-testid={`console-features-btn-${t.id}`} onClick={() => setFeatFor(t)}>
                      <ShieldCheck size={13} className="mr-1" /> Features
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" disabled={busy === t.id}
                            data-testid={`console-admins-btn-${t.id}`} onClick={() => setAdminsFor(t)}>
                      <UserPlus size={13} className="mr-1" /> Admins
                    </Button>
                    {t.plan_status === "suspended"
                      ? <Button size="sm" variant="outline" className="rounded-none h-8 text-xs text-emerald-700 border-emerald-300" disabled={busy === t.id}
                                data-testid={`console-activate-${t.id}`} onClick={() => patch(t.id, { plan_status: "active" }, "Account activated")}><Play size={13} className="mr-1" /> Activate</Button>
                      : <Button size="sm" variant="outline" className="rounded-none h-8 text-xs text-red-700 border-red-300" disabled={busy === t.id}
                                data-testid={`console-suspend-${t.id}`} onClick={() => patch(t.id, { plan_status: "suspended" }, "Account suspended")}><Prohibit size={13} className="mr-1" /> Suspend</Button>}
                    {cleanupMode && t.id !== "default" && !(t.is_internal || (t.name || "").toLowerCase().startsWith("claro")) && (
                      <Button size="sm" variant="outline" className="rounded-none h-8 text-xs text-red-700 border-red-400 bg-red-50 hover:bg-red-100" disabled={busy === t.id}
                              data-testid={`console-delete-${t.id}`} onClick={() => deleteEntity(t)}><Trash size={13} className="mr-1" /> Delete</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6 mt-6">
          <AiCostPanel />
          <AiTrendPanel />
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          <ActivityPanel tenants={data.tenants} />
        </TabsContent>

        <TabsContent value="announcements" className="mt-6">
          <AnnouncementsManager />
        </TabsContent>

        <TabsContent value="offboarding" className="mt-6">
          <ConsoleOffboarding />
        </TabsContent>

        <TabsContent value="trials" className="mt-6">
          <ConsoleTrials />
        </TabsContent>

        <TabsContent value="billing" className="mt-6">
          <ConsoleBilling />
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          <ConsoleInvoices />
        </TabsContent>

        <TabsContent value="nerc" className="mt-6">
          <div className="space-y-8">
            <ConsoleNerc />
            <ConsoleDuplicateCleanup />
            <div className="border-t border-border pt-8">
              <StandardsManager />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="key-security" className="mt-6">
          <ConsoleKeySecurity />
        </TabsContent>

        <TabsContent value="platform" className="mt-6">
          <PlatformModePanel />
        </TabsContent>

        <TabsContent value="security" className="space-y-6 mt-6">
          <div className="pt-2">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2"><ShieldCheck size={20} weight="duotone" /> Internal — Software Security</h2>
            <p className="text-sm text-muted-foreground mt-1">Claro-internal only. Not visible to customer administrators.</p>
          </div>

          <div className="border border-border bg-card p-6" data-testid="console-stability-report">
            <div className="flex items-start gap-3">
              <FileArrowDown size={22} className="mt-0.5 text-primary shrink-0" />
              <div className="flex-1">
                <h3 className="font-display font-bold text-lg">Verification of Stability</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Download a branded attestation (.docx) documenting our React hook-dependency stability checks
                  and backend static analysis — an internal trust signal for auditors and procurement.
                </p>
              </div>
              <Button data-testid="download-stability-report" onClick={() => window.open(`${API}/admin/stability-report`, "_blank")}
                className="rounded-none bg-primary hover:bg-primary/90 shrink-0">Download attestation</Button>
            </div>
          </div>

          <SecurityPosture />

          <PlatformBaseline />

          <SecurityAccessLog />
        </TabsContent>

        <TabsContent value="ai-transparency" className="mt-6">
          <ConsoleAiTransparency />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <ConsoleUsers />
        </TabsContent>

        <TabsContent value="test-scripts" className="mt-6">
          <TestScriptsPanel />
        </TabsContent>

        <TabsContent value="claro-tickets" className="mt-6">
          <ConsoleClaroTickets />
        </TabsContent>
      </Tabs>

      {creditFor && <CreditsDialog tenant={creditFor} onClose={() => setCreditFor(null)} onSaved={() => { setCreditFor(null); load(); }} />}
      {featFor && <FeaturesDialog tenant={featFor} onClose={() => setFeatFor(null)} onSaved={() => { setFeatFor(null); load(); }} />}
      <NewTenantDialog open={newTenantOpen} onOpenChange={setNewTenantOpen} plans={data.plans || []} onCreated={load} />
      <TenantAdminsDialog open={!!adminsFor} onOpenChange={(o) => !o && setAdminsFor(null)} tenant={adminsFor} onChanged={load} />
    </div>
  );
}

function ViewAsCustomerToggle() {
  const [on, setOn] = useState(() => localStorage.getItem("claro-view-as-customer") === "1");
  const toggle = (v) => {
    localStorage.setItem("claro-view-as-customer", v ? "1" : "0");
    setOn(v);
    window.dispatchEvent(new Event("claro:view-as-customer-changed"));
    if (v) toast.success("Entity View on — the app now mirrors the selected customer. ClaroConsole stays available.");
  };
  return (
    <div className="border border-indigo-200 bg-indigo-50/60 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="view-as-customer-panel">
      <div className="flex items-start gap-3">
        <Eye size={20} weight="duotone" className="text-indigo-700 mt-0.5" />
        <div>
          <div className="font-semibold text-sm text-foreground">View as customer (Entity View)</div>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
            Mirror the currently selected registered entity&apos;s exact toolbar and tools — their plan tier and hidden modules — everywhere in the app. ClaroConsole always stays reachable so you can switch back to the full Super-Admin view.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs font-medium text-muted-foreground">{on ? "On" : "Off"}</span>
        <Switch data-testid="view-as-customer-switch" checked={on} onCheckedChange={toggle} />
      </div>
    </div>
  );
}

function Stat({ label, value, testid, hi, hint }) {
  return (
    <div className={`border p-3 ${hi ? "border-emerald-200 bg-emerald-50" : "border-border"}`}>
      <div className="text-xs text-muted-foreground">
        {hint ? <HintLabel hint={hint} testid={testid ? `${testid}-hint` : undefined}>{label}</HintLabel> : label}
      </div>
      <div className={`text-lg font-bold ${hi ? "text-emerald-700" : "text-foreground"}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function AiCostPanel() {
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState("all");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/console/ai-usage?days=${days}&reason=${encodeURIComponent(reason)}`); setD(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load AI usage"); }
    finally { setLoading(false); }
  }, [days, reason]);
  useEffect(() => { load(); }, [load]);
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try { await downloadBlob(`/console/ai-usage.csv?days=${days}&reason=${encodeURIComponent(reason)}`, `ai-usage-${days}d.csv`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };
  const usd = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: Math.abs(n) < 1 ? 4 : 2, maximumFractionDigits: 4 })}`;
  const t = d?.totals;
  return (
    <div className="border border-border" data-testid="ai-cost-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <ChartBar size={18} weight="duotone" /> AI cost vs. credits — what a credit is worth to you
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="rounded-none h-8 w-48 text-xs" data-testid="ai-cost-reason-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {(d?.reason_options || []).map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-none h-8 text-xs" onClick={exportCsv} disabled={exporting} data-testid="ai-cost-export-csv">
            <DownloadSimple size={14} className="mr-1" /> {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="rounded-none h-8 w-36 text-xs" data-testid="ai-cost-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? <div className="p-4 text-sm text-muted-foreground" data-testid="ai-cost-loading">Loading…</div> : !d ? null : (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Real AI spend (est.)" value={usd(t.cost_usd)} testid="ai-cost-total" />
            <Stat label="Credits charged" value={(t.credits || 0).toLocaleString()} testid="ai-cost-credits" hint={AI_COST_HINTS.credits} />
            <Stat label="Avg cost / credit" value={usd(t.avg_cost_per_credit)} testid="ai-cost-per-credit" hi />
            <Stat label="AI calls" value={(t.calls || 0).toLocaleString()} testid="ai-cost-calls" hint={AI_COST_HINTS.calls} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">By action type</div>
            <table className="w-full text-sm" data-testid="ai-cost-by-action">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-1.5">Action</th>
                  <th className="py-1.5 text-right"><HintLabel hint={AI_COST_HINTS.credits}>Credits</HintLabel></th>
                  <th className="py-1.5 text-right">Est. cost</th>
                  <th className="py-1.5 text-right">Cost / credit</th>
                </tr>
              </thead>
              <tbody>
                {d.by_action.map((a) => (
                  <tr key={a.reason} className="border-b border-slate-50" data-testid={`ai-cost-action-${a.reason}`}>
                    <td className="py-1.5">{a.label}</td>
                    <td className="py-1.5 text-right">{(a.credits || 0).toLocaleString()}</td>
                    <td className="py-1.5 text-right">{usd(a.cost_usd)}</td>
                    <td className="py-1.5 text-right font-medium">{usd(a.cost_per_credit)}</td>
                  </tr>
                ))}
                {d.by_action.length === 0 && <tr><td colSpan={4} className="py-3 text-center text-muted-foreground">No AI usage in this window yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">By entity (per customer)</div>
            <table className="w-full text-sm" data-testid="ai-cost-by-tenant">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-1.5">Entity</th>
                  <th className="py-1.5 text-right"><HintLabel hint={AI_COST_HINTS.calls}>Calls</HintLabel></th>
                  <th className="py-1.5 text-right"><HintLabel hint={AI_COST_HINTS.credits}>Credits</HintLabel></th>
                  <th className="py-1.5 text-right">Est. cost</th>
                  <th className="py-1.5 text-right">Cost / credit</th>
                </tr>
              </thead>
              <tbody>
                {d.by_tenant.map((t2) => (
                  <tr key={t2.registration_id} className="border-b border-slate-50" data-testid={`ai-cost-tenant-${t2.registration_id}`}>
                    <td className="py-1.5">{t2.name || t2.registration_id}</td>
                    <td className="py-1.5 text-right">{(t2.calls || 0).toLocaleString()}</td>
                    <td className="py-1.5 text-right">{(t2.credits || 0).toLocaleString()}</td>
                    <td className="py-1.5 text-right">{usd(t2.cost_usd)}</td>
                    <td className="py-1.5 text-right font-medium">{usd(t2.cost_per_credit)}</td>
                  </tr>
                ))}
                {d.by_tenant.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">No AI usage in this window yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">By model (which LLM drives spend)</div>
            <table className="w-full text-sm" data-testid="ai-cost-by-model">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-1.5">Model</th>
                  <th className="py-1.5 text-right"><HintLabel hint={AI_COST_HINTS.calls}>Calls</HintLabel></th>
                  <th className="py-1.5 text-right">Input tok</th>
                  <th className="py-1.5 text-right">Output tok</th>
                  <th className="py-1.5 text-right">Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {(d.by_model || []).map((m) => (
                  <tr key={m.model} className="border-b border-slate-50" data-testid={`ai-cost-model-${m.model}`}>
                    <td className="py-1.5"><span className="font-medium">{m.model}</span> <span className="text-xs text-muted-foreground">· {m.provider}</span></td>
                    <td className="py-1.5 text-right">{(m.calls || 0).toLocaleString()}</td>
                    <td className="py-1.5 text-right">{(m.input_tokens || 0).toLocaleString()}</td>
                    <td className="py-1.5 text-right">{(m.output_tokens || 0).toLocaleString()}</td>
                    <td className="py-1.5 text-right font-medium">{usd(m.cost_usd)}</td>
                  </tr>
                ))}
                {(!d.by_model || d.by_model.length === 0) && <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">No AI usage in this window yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">{d.note}</p>
        </div>
      )}
    </div>
  );
}

const TREND_COLORS = ["#071B3E", "#0ea5e9", "#16a34a", "#d97706", "#9333ea", "#dc2626", "#0891b2", "#65a30d"];
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtMonth = (lbl) => { const [y, m] = String(lbl).split("-"); return `${MONTH_ABBR[Number(m) - 1] || m} ${String(y).slice(2)}`; };
const usdShort = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: Math.abs(n) < 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

function AiTrendPanel() {
  const [months, setMonths] = useState(6);
  const [scope, setScope] = useState("all");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `/console/ai-usage/trend?months=${months}${scope !== "all" ? `&registration_id=${encodeURIComponent(scope)}` : ""}`;
      const { data } = await api.get(q);
      setD(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load AI trend"); }
    finally { setLoading(false); }
  }, [months, scope]);
  useEffect(() => { load(); }, [load]);

  // Combined area data — actual monthly spend + a dashed projection segment on the current month.
  const combinedData = useMemo(() => {
    if (!d?.combined) return [];
    const arr = d.combined.map((p) => ({ ...p, label: fmtMonth(p.month) }));
    // build a 2-point projection line: prev-complete month actual -> current month projected
    const curIdx = arr.findIndex((p) => p.is_current);
    return arr.map((p, i) => ({
      label: p.label,
      actual: p.cost_usd,
      projection: p.is_current ? p.projected_eom : (curIdx > 0 && i === curIdx - 1 ? p.cost_usd : null),
    }));
  }, [d]);

  // Per-entity comparison lines (top entities). One data row per month, a key per entity.
  const topEntities = useMemo(() => (d?.entities || []).slice(0, TREND_COLORS.length), [d]);
  const entityData = useMemo(() => {
    if (!d?.months) return [];
    return d.months.map((lbl) => {
      const row = { label: fmtMonth(lbl) };
      topEntities.forEach((e) => {
        const pt = (e.series || []).find((s) => s.month === lbl);
        row[e.registration_id] = pt ? pt.cost_usd : 0;
      });
      return row;
    });
  }, [d, topEntities]);

  const orgProjected = useMemo(() => {
    const cur = (d?.combined || []).find((p) => p.is_current);
    return cur ? cur.projected_eom : 0;
  }, [d]);
  const orgSoFar = useMemo(() => {
    const cur = (d?.combined || []).find((p) => p.is_current);
    return cur ? cur.cost_usd : 0;
  }, [d]);

  return (
    <div className="border border-border mt-4" data-testid="ai-trend-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <ChartBar size={18} weight="duotone" /> AI spend trend &amp; forecast
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="rounded-none h-8 w-52 text-xs" data-testid="ai-trend-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers (org-wide)</SelectItem>
              {(d?.entities || []).map((e) => (
                <SelectItem key={e.registration_id} value={e.registration_id}>{e.name || e.registration_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="rounded-none h-8 w-36 text-xs" data-testid="ai-trend-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? <div className="p-4 text-sm text-muted-foreground" data-testid="ai-trend-loading">Loading…</div> : !d ? null : (
        <div className="p-4 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Stat label={`This month so far (${d.day}/${d.days_in_month})`} value={usdShort(orgSoFar)} testid="ai-trend-so-far" />
            <Stat label="Projected end-of-month" value={usdShort(orgProjected)} testid="ai-trend-projected" hi />
            <Stat label={`${months}-month total`} value={usdShort((d.combined || []).reduce((s, p) => s + (p.cost_usd || 0), 0))} testid="ai-trend-total" />
          </div>

          <div data-testid="ai-trend-combined-chart">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
              Monthly spend {scope === "all" ? "(org-wide)" : `— ${(d.entities?.[0]?.name) || "entity"}`} · dashed = projected end-of-month
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={combinedData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#071B3E" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#071B3E" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => usdShort(v)} width={56} />
                <RTooltip formatter={(v, name) => [usdShort(v), name === "projection" ? "Projected EOM" : "Actual spend"]}
                          contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #e2e8f0" }} />
                <Area type="monotone" dataKey="actual" name="Actual spend" stroke="#071B3E" strokeWidth={2} fill="url(#trendFill)" dot={{ r: 2.5 }} />
                <Line type="monotone" dataKey="projection" name="Projected EOM" stroke="#16a34a" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3, fill: "#16a34a" }} connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {scope === "all" && topEntities.length > 0 && (
            <div data-testid="ai-trend-entity-chart">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">By customer — monthly spend comparison</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={entityData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => usdShort(v)} width={56} />
                  <RTooltip formatter={(v) => usdShort(v)} contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #e2e8f0" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {topEntities.map((e, i) => (
                    <Line key={e.registration_id} type="monotone" dataKey={e.registration_id}
                          name={e.name || e.registration_id} stroke={TREND_COLORS[i % TREND_COLORS.length]}
                          strokeWidth={2} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{d.note}</p>
        </div>
      )}
    </div>
  );
}

function CreditsDialog({ tenant, onClose, onSaved }) {
  const [bucket, setBucket] = useState("topup");
  const [action, setAction] = useState("add");
  const [amount, setAmount] = useState(500);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/console/tenants/${tenant.id}/credits`, {
        bucket, action, amount: Number(amount) || 0, reason,
      });
      toast.success("AI credits updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none" data-testid="console-credits-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Coins size={18} /> AI Credits — {tenant.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-xs text-muted-foreground">Current: {tenant.ai_credits_base.toLocaleString()} base + {tenant.ai_credits_topup.toLocaleString()} top-up</div>
          <div>
            <Label className="text-xs">Bucket</Label>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="rounded-none" data-testid="console-credit-bucket"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="base">Monthly base (resets)</SelectItem>
                <SelectItem value="topup">Purchased top-up (rolls over 1 yr)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="rounded-none" data-testid="console-credit-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="add">Add</SelectItem>
                <SelectItem value="set">Set to exact</SelectItem>
                {bucket === "base" && <SelectItem value="reset_to_plan">Reset to plan default</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          {action !== "reset_to_plan" && (
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-none" data-testid="console-credit-amount" />
            </div>
          )}
          <div>
            <Label className="text-xs">Reason (ledger note)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., wire payment received" className="rounded-none" data-testid="console-credit-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="console-credit-save">{saving ? "Saving…" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtDur(sec) {
  sec = Number(sec || 0);
  if (sec < 60) return `${sec}s`;
  const h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}
function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function ActivityPanel({ tenants = [] }) {
  const [days, setDays] = useState(30);
  const [reg, setReg] = useState("all");
  const [evt, setEvt] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (reg !== "all") params.set("registration_id", reg);
      if (evt !== "all") params.set("event", evt);
      const { data } = await api.get(`/console/activity?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load activity");
    } finally { setLoading(false); }
  }, [days, reg, evt]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const params = new URLSearchParams({ days: String(days) });
    if (reg !== "all") params.set("registration_id", reg);
    if (evt !== "all") params.set("event", evt);
    try { await downloadBlob(`/console/activity/export.csv?${params.toString()}`, "activity.csv"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const summary = data?.summary || [];

  return (
    <div className="border border-border p-4" data-testid="activity-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><ChartBar size={16} weight="duotone" /> User activity & time tracking</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={reg} onValueChange={setReg}>
            <SelectTrigger className="rounded-none h-8 w-48 text-xs" data-testid="activity-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={evt} onValueChange={setEvt}>
            <SelectTrigger className="rounded-none h-8 w-36 text-xs" data-testid="activity-event-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              <SelectItem value="login">Logins</SelectItem>
              <SelectItem value="page_view">Page views</SelectItem>
              <SelectItem value="ai_action">AI actions</SelectItem>
              <SelectItem value="logout">Logouts</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="rounded-none h-8 w-28 text-xs" data-testid="activity-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={exportCsv} data-testid="activity-export-csv">
            <DownloadSimple size={13} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      {loading && <div className="text-center text-muted-foreground py-6 text-sm">Loading…</div>}
      {!loading && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="overflow-x-auto">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Per-user summary</div>
            <table className="w-full text-xs" data-testid="activity-summary-table">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="p-1.5">User</th><th className="p-1.5">Logins</th><th className="p-1.5">Pages</th>
                  <th className="p-1.5">AI</th><th className="p-1.5">Credits</th><th className="p-1.5">Time</th><th className="p-1.5">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No activity in range.</td></tr>}
                {summary.map((s) => (
                  <tr key={s.email} className="border-b border-border">
                    <td className="p-1.5"><div className="font-medium text-foreground">{s.name || s.email}</div><div className="text-muted-foreground">{s.email}</div></td>
                    <td className="p-1.5">{s.logins}</td>
                    <td className="p-1.5">{s.page_views}</td>
                    <td className="p-1.5">{s.ai_actions}</td>
                    <td className="p-1.5">{s.ai_credits}</td>
                    <td className="p-1.5">{fmtDur(s.active_seconds)}</td>
                    <td className="p-1.5 whitespace-nowrap">{fmtWhen(s.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Recent events ({data?.total_events || 0})</div>
            <div className="max-h-72 overflow-y-auto border border-border" data-testid="activity-events-list">
              {(data?.events || []).slice(0, 200).map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-2 px-2 py-1 text-xs border-b border-slate-50">
                  <span className="truncate"><span className="font-medium text-foreground">{e.email || "—"}</span> <span className="text-muted-foreground">{e.event}</span> <span className="text-muted-foreground">{e.path || e.detail || e.reason || ""}</span></span>
                  <span className="text-muted-foreground whitespace-nowrap">{fmtWhen(e.at)}</span>
                </div>
              ))}
              {(data?.events || []).length === 0 && <div className="p-4 text-center text-muted-foreground text-xs">No events.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeaturesDialog({ tenant, onClose, onSaved }) {
  const [overrides, setOverrides] = useState(tenant.feature_overrides || {});
  const [expires, setExpires] = useState((tenant.access_expires_at || "").slice(0, 10));
  const [billing, setBilling] = useState(tenant.billing_mode && tenant.billing_mode !== "stripe" ? tenant.billing_mode : "manual_wire");
  const [slug, setSlug] = useState(tenant.slug || "");
  const [aiFiles, setAiFiles] = useState(tenant.ai_limits?.max_files || "");
  const [aiMb, setAiMb] = useState(tenant.ai_limits?.max_upload_mb || "");
  const [saving, setSaving] = useState(false);
  const eff = tenant.ai_limits_effective || {};

  const featMode = (k) => (k in overrides ? (overrides[k] ? "on" : "off") : "default");
  const setFeat = (k, mode) => setOverrides((s) => {
    const next = { ...s };
    if (mode === "default") delete next[k];
    else next[k] = mode === "on";
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/console/tenants/${tenant.id}`, {
        feature_overrides: overrides,
        access_expires_at: expires ? new Date(expires + "T23:59:59Z").toISOString() : "",
        billing_mode: billing, slug,
        ai_limits: {
          ...(aiFiles ? { max_files: Number(aiFiles) } : {}),
          ...(aiMb ? { max_upload_mb: Number(aiMb) } : {}),
        },
      });
      toast.success("Tenant updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none" data-testid="console-features-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} /> Features & Access — {tenant.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <Label className="text-xs">Subdomain slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} className="rounded-none font-mono" data-testid="console-slug" />
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Feature access</div>
            <p className="text-[11px] text-muted-foreground mb-2">Leave on <b>Follow plan default</b> so the feature turns on/off automatically with the plan. Only force On/Off to override the plan for this one tenant.</p>
            <div className="space-y-2">
              {FEATURE_KEYS.map((k) => (
                <div key={k} className="flex items-center justify-between border border-border px-2 py-1.5 gap-3">
                  <span className="text-xs capitalize">{k.replace(/_/g, " ")}</span>
                  <Select value={featMode(k)} onValueChange={(v) => setFeat(k, v)}>
                    <SelectTrigger className="rounded-none h-8 w-44 text-xs shrink-0" data-testid={`console-feat-${k}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Follow plan default</SelectItem>
                      <SelectItem value="on">Force on</SelectItem>
                      <SelectItem value="off">Force off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">AI attachment limits</div>
            <p className="text-[11px] text-muted-foreground mb-2">Caps files &amp; upload size per AI analysis (anti-gaming). Leave blank to follow the plan default. Effective now: <b>{eff.max_files} files</b> · <b>{eff.max_upload_mb} MB</b>/file.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Max files / analysis</Label>
                <Input type="number" min="1" value={aiFiles} onChange={(e) => setAiFiles(e.target.value)} placeholder={`plan: ${eff.max_files}`} className="rounded-none" data-testid="console-ai-max-files" />
              </div>
              <div>
                <Label className="text-xs">Max MB / file</Label>
                <Input type="number" min="1" value={aiMb} onChange={(e) => setAiMb(e.target.value)} placeholder={`plan: ${eff.max_upload_mb}`} className="rounded-none" data-testid="console-ai-max-mb" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Billing mode</Label>
              <Select value={billing} onValueChange={setBilling}>
                <SelectTrigger className="rounded-none" data-testid="console-billing-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="manual_wire">Manual / Wire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Access expires (wire clients)</Label>
              <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="rounded-none" data-testid="console-access-expires" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="console-features-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
